import { useTranslation } from 'react-i18next';
import { Icon, Gauge, Map as MapIcon } from '../ui/Icon';

interface Props {
  /** `quota` = our monthly Maps cap is spent. `error` = Google itself wouldn't load. */
  reason: 'quota' | 'error';
  /** ISO date the cap resets, when the backend told us. */
  resetsAt?: string | null;
}

/**
 * What the map area shows when there is no map to show. A blank grey rectangle
 * reads as a broken page; hitting the monthly cap is a normal, temporary thing
 * and deserves an explanation and a date.
 */
export default function MapUnavailable({ reason, resetsAt }: Props) {
  const { t } = useTranslation();
  const isQuota = reason === 'quota';
  const date = resetsAt ? new Date(resetsAt).toLocaleDateString() : null;

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <div className="max-w-sm text-center">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
          isQuota
            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-500'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
        }`}>
          <Icon as={isQuota ? Gauge : MapIcon} size={26} />
        </div>

        <h3 className="font-display font-semibold text-lg text-slate-900 dark:text-slate-100 mb-2">
          {isQuota ? t('map.quota.title') : t('map.unavailable.title')}
        </h3>

        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
          {isQuota
            ? (date ? t('map.quota.bodyWithDate', { date }) : t('map.quota.body'))
            : t('map.unavailable.body')}
        </p>

        {isQuota && (
          <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">{t('map.quota.hint')}</p>
        )}
      </div>
    </div>
  );
}
